/**
 * FTP / SFTP client state (zustand). Connections are the Vault's `sftp`-typed
 * item envelopes. A stored connection is never decrypted here: it connects by
 * its id (`ftp_connect_vault`, card 6.4) and Rust reads the credential. Only a
 * temporary connection's body — typed by the person, never stored — passes
 * through `ftp_connect`.
 * Sessions, directory listings and transfer bookkeeping live Rust-side behind
 * the ftp_* commands (plans/25_07_2026/vault-suite/CONTRACTS.md §2); this
 * store is a thin cache over vault_list + ftp_list_dir/ftp_local_list plus
 * the `ftp:transfer_progress` event stream.
 */
import { create } from 'zustand';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { openVaultGate, vaultErrorToken, VAULT_GATE_UNLOCKED_EVENT } from './vaultStore';
import { isTeamItemKey } from '../services/teamVaultService';
import { usePreferencesStore } from './preferencesStore';
import { recordFeature, ACTION } from '../services/telemetry';
import { checkConnection, markLost, watchConnectionLost } from './ftpConnectionLost';

// Shape of a per-connection remote pane; also the stable fallback the view
// reads when the active connection has no pane yet. Never mutated (frozen).
export const EMPTY_REMOTE_PANE = Object.freeze({
  path: '',
  entries: [],
  loading: false,
  error: null,
  selection: [],
});

const initialState = {
  // True when vault_status says the vault is locked (connections unreadable).
  vaultLocked: false,
  unlocking: false,
  // Vault envelopes of type 'sftp' (id, name, index: {protocol, host, port,
  // username}, …) — no secret values.
  connections: [],
  connectionsLoaded: false,
  activeConnId: null,
  // connId -> 'disconnected' | 'connecting' | 'connected' | 'error' | 'lost'
  // ('lost' = the server stopped answering; see ftpConnectionLost.js)
  connState: {},
  // connId -> last error message (only meaningful while connState is 'error')
  connError: {},
  // Remote pane state keyed PER connId so switching between two connected
  // sessions always shows that connection's own listing (never the other
  // server's). Each value: { path, entries, loading, error, selection }.
  remotePanes: {},
  // Monotonic per-conn request counter — a slow ftp_list_dir resolving after a
  // newer navigation/switch is dropped instead of painting stale entries.
  remoteReqSeq: {},
  // Local pane (single — the OS filesystem is shared across connections)
  localPath: '',
  localEntries: [],
  localLoading: false,
  localError: null,
  localSelection: [],
  // Monotonic request counter for the local pane (same staleness guard).
  localReqSeq: 0,
  // Bumped on every reset() (logout). In-flight async loops capture it and
  // bail once it changes so they can't repopulate a fresh store.
  generation: 0,
  // [{ transferId, connId, kind: 'upload'|'download', name, transferred,
  //    total, done, error, canceled }]
  transfers: [],
  /** Session-only rows, in the same envelope shape a vault item has. */
  tempConnections: [],
};

const errText = (e) => String(e?.message || e || 'FTP error');

/**
 * A temporary connection's body — the one the person just typed, held in this
 * module for the session. Stored connections never come through here: they
 * connect by id and Rust reads the credential (`ftp_connect_vault`).
 */
function tempConnectionData(connId) {
  const body = tempBodies.get(connId);
  if (!body) throw new Error('temp_connection_gone');
  return body;
}

/** Directories first, then case-insensitive name (FileZilla ordering). */
function sortEntries(list) {
  return [...(Array.isArray(list) ? list : [])].sort((a, b) => {
    if (!!a.isDir !== !!b.isDir) return a.isDir ? -1 : 1;
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
      sensitivity: 'base',
    });
  });
}

/** A dotfile by the POSIX convention — the only definition any of the three
 *  listing sources agree on. `.` and `..` never reach here; both list paths
 *  drop them in `sanitize_listing_name`. */
export function isHiddenEntry(entry) {
  return String(entry?.name || '').startsWith('.');
}

/**
 * Drop dotfiles unless the preference says otherwise.
 *
 * This runs client-side for ALL THREE sources, and it has to, because they do
 * not agree at the source: SFTP `readdir` and the local `read_dir` always
 * return dotfiles, while plain FTP's `LIST` omits them until the server is
 * asked with `-a`. Filtering here is what makes the toggle mean one thing in
 * every pane instead of "depends which protocol you happen to be on".
 *
 * The FTP `-a` request is therefore necessary but not sufficient: it gets the
 * entries onto the wire, and this decides whether they are shown.
 */
function applyHiddenFilter(list, showHidden) {
  const arr = Array.isArray(list) ? list : [];
  return showHidden ? arr : arr.filter((e) => !isHiddenEntry(e));
}

/** Read the pref live. Deliberately not held in this store: the toggle lives in
 *  app preferences (persisted, shared with the Settings surface), and caching a
 *  copy here is how the two drift apart. */
function showHiddenPref() {
  try {
    return usePreferencesStore.getState().ftpShowHiddenFiles !== false;
  } catch {
    // Matches the default rather than the safest-looking value: a store that
    // failed to read should behave like a fresh install, not silently hide
    // the files this pane is usually opened to find.
    return true;
  }
}

// Auto-remove succeeded transfer rows ~8s after their terminal event so the
// strip doesn't accumulate; failed/canceled rows stay until "Clear".
const autoRemoveTimers = new Map();
function scheduleAutoRemove(transferId) {
  if (autoRemoveTimers.has(transferId)) return;
  autoRemoveTimers.set(
    transferId,
    setTimeout(() => {
      autoRemoveTimers.delete(transferId);
      useFtpStore.setState((s) => ({
        transfers: s.transfers.filter(
          // A row inside a folder upload stays: the group's "n of m files"
          // counts it, and it leaves with the group ("Clear finished").
          (t) => !(t.transferId === transferId && t.done && !t.error && !t.canceled && !t.groupId)
        ),
      }));
    }, 8000)
  );
}
function clearAutoRemoveTimers() {
  for (const timer of autoRemoveTimers.values()) clearTimeout(timer);
  autoRemoveTimers.clear();
}

/**
 * TEMPORARY CONNECTIONS — dial something once without putting it in the vault.
 *
 * Every row in this sidebar used to be a vault item, so trying a server meant
 * first committing it to your vault and then cleaning it up. The DB Manager has
 * had the ad-hoc route for a while; this is the same idea (owner, 23.09.2026).
 *
 * The credential body lives HERE, in a module-level map, and not in the zustand
 * state: state is what gets logged, snapshotted and handed to devtools, and a
 * password has no business in any of those. It is session-only by construction
 * — a module variable dies with the page, so a reload leaves nothing behind.
 *
 * This is not a new exposure. `ftp_connect` takes host/username/password as
 * arguments and does no vault access of its own, so a credential already
 * round-trips through the webview on every FTP dial (see `connect` below). What
 * is new is only that this one is never written down.
 */
const tempBodies = new Map();

let tempSeq = 0;
/** `temp:` prefixed so it can never be mistaken for a vault id, or a team key. */
const newTempConnId = () => `temp:${Date.now().toString(36)}-${(tempSeq += 1)}`;

export const isTempConnId = (id) => String(id || '').startsWith('temp:');

/** The body for a temporary connection, or null. Never leaves this module. */
export const readTempConnBody = (id) => tempBodies.get(id) || null;

export const useFtpStore = create((set, get) => ({
  ...initialState,

  setActiveConnId: (activeConnId) => set({ activeConnId }),

  /**
   * Add a connection for this session only.
   *
   * `data` is the same body a vault sftp item holds (protocol, host, port,
   * username, password/privateKey/passphrase, initialPath). The secret-bearing
   * half goes to `tempBodies`; what lands in the store is an ENVELOPE shaped
   * exactly like `vault_list` returns, so every consumer — the sidebar rows,
   * the search filter, the summary line — treats it like any other row without
   * learning a new shape.
   *
   * @returns {string} the new connection's id
   */
  addTempConnection: ({ name, data }) => {
    const id = newTempConnId();
    const body = { ...(data || {}) };
    tempBodies.set(id, body);
    set((s) => ({
      tempConnections: [
        ...s.tempConnections,
        {
          id,
          name: name || body.host || 'Connection',
          type: 'sftp',
          temporary: true,
          // The PLAINTEXT index a vault envelope carries, and nothing more:
          // this is what the row renders, and it must not gain the password.
          index: {
            protocol: body.protocol || 'sftp',
            host: body.host || '',
            port: body.port || '',
            username: body.username || '',
          },
        },
      ],
    }));
    return id;
  },

  /** Drop a temporary connection and its body. No vault call — there is none. */
  removeTempConnection: (id) => {
    tempBodies.delete(id);
    set((s) => ({
      tempConnections: s.tempConnections.filter((c) => c.id !== id),
      activeConnId: s.activeConnId === id ? null : s.activeConnId,
    }));
  },
  setLocalSelection: (localSelection) => set({ localSelection }),
  setRemoteSelection: (remoteSelection) => {
    const connId = get().activeConnId;
    if (!connId) return;
    set((s) => {
      const pane = s.remotePanes[connId] || EMPTY_REMOTE_PANE;
      return { remotePanes: { ...s.remotePanes, [connId]: { ...pane, selection: remoteSelection } } };
    });
  },

  /** Current remote directory of the active connection ('' when none). */
  getRemotePath: () => (get().remotePanes[get().activeConnId] || EMPTY_REMOTE_PANE).path,

  /**
   * Reload the connection list from the vault (envelopes only). Filters to
   * sftp-typed items and hides internal "__"-prefixed names (CONTRACTS §3).
   * A locked vault flips `vaultLocked` instead of erroring.
   */
  loadConnections: async () => {
    if (!isTauri()) return;
    try {
      const status = await invoke('vault_status');
      if (!status?.unlocked) {
        // Locking blocks NEW dials; it does not tear down what is already
        // open (lock plan D2 — same rule DB Manager and the Terminal follow).
        // A live SFTP session holds its credentials in the Rust session, not
        // in the vault, so it keeps working — but its ROW rendered from this
        // list, so clearing the whole list made the open session unreachable
        // and FtpView fell back to the locked screen mid-transfer. Keep the
        // rows whose sessions are live; only the rest is vault-derived and
        // unreadable while locked.
        set((s) => ({
          vaultLocked: true,
          connections: s.connections.filter((c) => {
            const st = s.connState[c.id];
            return st === 'connected' || st === 'connecting';
          }),
          connectionsLoaded: true,
        }));
        return;
      }
      const list = await invoke('vault_list');
      const connections = (Array.isArray(list) ? list : []).filter(
        (it) => it?.type === 'sftp' && !String(it?.name || '').startsWith('__')
      );
      set((s) => ({
        vaultLocked: false,
        connections,
        connectionsLoaded: true,
        activeConnId:
          s.activeConnId && connections.some((c) => c.id === s.activeConnId)
            ? s.activeConnId
            : null,
      }));
    } catch (e) {
      console.warn('[ftp] loadConnections failed:', errText(e));
      set({ connectionsLoaded: true });
    }
  },

  /**
   * Unlock the vault (auto-initializing) then reload the connection list.
   * When a Protected vault answers `passphrase_required` this opens the
   * passphrase gate and returns — the module-level `vault-gate-unlocked`
   * listener below reloads the list the moment the gate succeeds, so the
   * user never has to press Unlock a second time.
   */
  unlockVault: async () => {
    if (!isTauri()) return false;
    set({ unlocking: true });
    try {
      await invoke('vault_unlock');
      recordFeature(ACTION.VAULT_UNLOCK);   // this path skips the gate
      await get().loadConnections();
      return true;
    } catch (e) {
      // A Protected vault with no usable device cache answers
      // `passphrase_required`. That is a request for the passphrase, not a
      // failure: without this the FTP panel's Unlock button would do nothing
      // at all except write a console warning nobody sees. `vault_needs_bootstrap`
      // (a genuinely fresh, never-bootstrapped scope) and `vault_kdf_corrupt`
      // (a protected vault whose kdf block cannot open with any secret) are
      // the same shape of problem — a request for the gate, not a fault to
      // log and swallow (vault-storage-transition phase 7).
      if (
        vaultErrorToken(e) === 'passphrase_required' ||
        vaultErrorToken(e) === 'vault_needs_bootstrap' ||
        vaultErrorToken(e) === 'vault_kdf_corrupt'
      ) {
        openVaultGate('ftp');
        return false;
      }
      console.warn('[ftp] vault unlock failed:', errText(e));
      return false;
    } finally {
      set({ unlocking: false });
    }
  },

  /**
   * Connect a connection row, then land in its initialPath (falling back to
   * the session's home dir).
   *
   * A STORED row — personal or team (`team:<team>:<item>`) — connects by its id
   * alone: `ftp_connect_vault` reads the credential in Rust and answers only
   * `{home, initialPath}`, the way the Terminal's `ssh_term_open` always has
   * (card 6.4). Only a temporary connection, whose body the person typed for
   * this session, hands host/user/password to `ftp_connect`.
   *
   * `row` (optional) says which vault the row belongs to: a team row must carry
   * a team key, or nothing is sent (a team id must never be looked up in the
   * personal table).
   */
  connect: async (connId, row = null) => {
    if (!isTauri() || !connId) return;
    watchConnectionLost(useFtpStore);
    const state = get().connState[connId];
    if (state === 'connecting') {
      set({ activeConnId: connId });
      return;
    }
    if (state === 'connected') {
      // The pane for this conn already holds its own listing — just show it.
      set({ activeConnId: connId });
      const pane = get().remotePanes[connId];
      if (!pane || !pane.path) await get().listRemote('/').catch(() => {});
      return;
    }
    set((s) => ({
      activeConnId: connId,
      connState: { ...s.connState, [connId]: 'connecting' },
      connError: { ...s.connError, [connId]: null },
      remotePanes: { ...s.remotePanes, [connId]: { ...EMPTY_REMOTE_PANE, loading: true } },
    }));
    try {
      let home;
      let initialPath;
      if (isTempConnId(connId)) {
        const d = tempConnectionData(connId);
        ({ home } = await invoke('ftp_connect', {
          connId,
          protocol: d.protocol || 'sftp',
          host: d.host,
          port: d.port,
          username: d.username,
          password: d.password ?? null,
          privateKey: d.privateKey ?? null,
          passphrase: d.passphrase ?? null,
        }));
        initialPath = d.initialPath;
      } else {
        if (row?.origin === 'team' && !isTeamItemKey(connId)) throw new Error('team_vault_bad_item_id');
        ({ home, initialPath } = await invoke('ftp_connect_vault', { connId }));
      }
      set((s) => ({ connState: { ...s.connState, [connId]: 'connected' } }));
      const homePath = home || '/';
      const startPath = String(initialPath || '').trim() || homePath;
      try {
        await get().listRemote(startPath);
      } catch {
        // A stale initialPath shouldn't strand the session — fall back home.
        if (startPath !== homePath) await get().listRemote(homePath).catch(() => {});
      }
      if (!get().localPath) await get().listLocal(null).catch(() => {});
    } catch (e) {
      set((s) => ({
        connState: { ...s.connState, [connId]: 'error' },
        connError: { ...s.connError, [connId]: errText(e) },
      }));
    }
  },

  disconnect: async (connId) => {
    const id = connId || get().activeConnId;
    if (!id) return;
    try {
      await invoke('ftp_disconnect', { connId: id });
    } catch (e) {
      console.warn('[ftp] disconnect failed:', errText(e));
    }
    set((s) => {
      const remotePanes = { ...s.remotePanes };
      delete remotePanes[id];
      const remoteReqSeq = { ...s.remoteReqSeq };
      delete remoteReqSeq[id];
      return {
        connState: { ...s.connState, [id]: 'disconnected' },
        connError: { ...s.connError, [id]: null },
        remotePanes,
        remoteReqSeq,
      };
    });
  },

  /** List a remote directory on the active session. Rejects on failure (the
   *  error is also surfaced via the pane's error) so callers can chain/fallback.
   *  Writes go to the pane of the conn captured at call time, and a stale
   *  resolution (superseded by a newer list on the same conn) is dropped. */
  listRemote: async (path) => {
    const connId = get().activeConnId;
    if (!connId || !path) return;
    const seq = (get().remoteReqSeq[connId] || 0) + 1;
    set((s) => ({
      remoteReqSeq: { ...s.remoteReqSeq, [connId]: seq },
      remotePanes: {
        ...s.remotePanes,
        [connId]: { ...(s.remotePanes[connId] || EMPTY_REMOTE_PANE), loading: true, error: null },
      },
    }));
    try {
      const showHidden = showHiddenPref();
      // `showHidden` reaches Rust only to make plain FTP ask with `LIST -a`;
      // SFTP already returns dotfiles. The filter below is what both obey.
      const entries = await invoke('ftp_list_dir', { connId, path, showHidden });
      if (get().remoteReqSeq[connId] !== seq) return; // superseded — drop
      set((s) => ({
        remotePanes: {
          ...s.remotePanes,
          [connId]: {
            path,
            entries: sortEntries(applyHiddenFilter(entries, showHidden)),
            loading: false,
            error: null,
            selection: [],
          },
        },
      }));
    } catch (e) {
      if (get().remoteReqSeq[connId] === seq) {
        set((s) => ({
          remotePanes: {
            ...s.remotePanes,
            [connId]: {
              ...(s.remotePanes[connId] || EMPTY_REMOTE_PANE),
              loading: false,
              error: errText(e),
            },
          },
        }));
      }
      // A failure can be the server gone (a night asleep): ask, don't wait
      // for the background check.
      get().checkConnection(connId);
      throw e;
    }
  },

  /** True while `connId` answers; a dead session becomes 'lost'. */
  checkConnection: (connId) => checkConnection(useFtpStore, connId),

  /** The server stopped answering (Rust's `ftp:connection_lost`). */
  markLost: (connId, reason) => markLost(useFtpStore, connId, reason),

  /** List a local directory (null = OS home dir). Rejects on failure. A stale
   *  resolution superseded by a newer navigation is dropped. */
  listLocal: async (path) => {
    if (!isTauri()) return;
    const seq = get().localReqSeq + 1;
    set({ localReqSeq: seq, localLoading: true, localError: null });
    try {
      const res = await invoke('ftp_local_list', { path: path ?? null });
      if (get().localReqSeq !== seq) return; // superseded — drop
      set({
        localPath: res?.path || '',
        localEntries: sortEntries(applyHiddenFilter(res?.entries, showHiddenPref())),
        localLoading: false,
        localSelection: [],
      });
    } catch (e) {
      if (get().localReqSeq === seq) set({ localLoading: false, localError: errText(e) });
      throw e;
    }
  },

  /** Remote mkdir + refresh. Errors propagate (modals show them inline). */
  mkdirRemote: async (path) => {
    const connId = get().activeConnId;
    if (!connId) return;
    await invoke('ftp_mkdir', { connId, path });
    await get().listRemote(get().getRemotePath());
  },

  /**
   * Create an EMPTY remote file + refresh. Errors propagate.
   *
   * `ftp_write_text` with no `expectedMtime` skips the conflict check and
   * truncates whatever is at `path`, which is right for Save in the editor and
   * catastrophic for "New file". **The caller checks the current listing for a
   * collision first** — a name check against what the user is looking at, so
   * the refusal needs no extra round trip and no existence probe.
   */
  createRemoteFile: async (path) => {
    const connId = get().activeConnId;
    if (!connId) return;
    await invoke('ftp_write_text', { connId, path, content: '', expectedMtime: null });
    await get().listRemote(get().getRemotePath());
  },

  /** Remote rename/move + refresh. Errors propagate. */
  renameRemote: async (from, to) => {
    const connId = get().activeConnId;
    if (!connId) return;
    await invoke('ftp_rename', { connId, from, to });
    await get().listRemote(get().getRemotePath());
  },

  /** Remote delete (recursive for dirs, Rust-side) + refresh. Errors propagate. */
  deleteRemote: async (path, isDir) => {
    const connId = get().activeConnId;
    if (!connId) return;
    await invoke('ftp_delete', { connId, path, isDir: !!isDir });
    await get().listRemote(get().getRemotePath());
  },

  /** SFTP-only chmod + refresh. Errors propagate. */
  chmodRemote: async (path, mode) => {
    const connId = get().activeConnId;
    if (!connId) return;
    await invoke('ftp_chmod', { connId, path, mode });
    await get().listRemote(get().getRemotePath());
  },

  /**
   * Merge a transfer row by transferId — fed by the global
   * `ftp:transfer_progress` listener (FtpView) and by optimistic local rows
   * created when a transfer command is issued. Succeeded rows self-expire.
   *
   * A terminal row (done || canceled) is sticky: later/raced patches may add
   * fields but can never flip done/canceled back to false, so a slow chunk-loop
   * progress upsert can't resurrect a cancelled/finished transfer.
   */
  upsertTransfer: (payload) => {
    if (!payload || !payload.transferId) return;
    set((s) => {
      const idx = s.transfers.findIndex((t) => t.transferId === payload.transferId);
      if (idx < 0) {
        return { transfers: [...s.transfers, { ...payload, sampleAt: Date.now() }] };
      }
      const existing = s.transfers[idx];
      const merged = { ...existing, ...payload };
      // Speed, from two consecutive progress events. Computed HERE rather than
      // in the row, because the row re-renders for reasons that have nothing to
      // do with new bytes and would divide by a shrinking interval each time.
      const now = Date.now();
      const dt = (now - (existing.sampleAt || now)) / 1000;
      const db = (Number(payload.transferred) || 0) - (Number(existing.transferred) || 0);
      if (dt >= 0.25 && db > 0) {
        // Exponential smoothing. A raw per-tick rate on a chunked transfer
        // swings by an order of magnitude between ticks and reads as noise;
        // this keeps it legible while still reacting within a second or two.
        const instant = db / dt;
        merged.bps = existing.bps ? existing.bps * 0.7 + instant * 0.3 : instant;
        merged.sampleAt = now;
      }
      if (existing.done || existing.canceled) {
        merged.done = existing.done || payload.done || false;
        merged.canceled = existing.canceled || payload.canceled || false;
        // Keep an already-recorded terminal error unless the patch replaces it.
        if (existing.error && payload.error == null) merged.error = existing.error;
      }
      return { transfers: s.transfers.map((t, i) => (i === idx ? merged : t)) };
    });
    if (payload.done && !payload.error && !payload.canceled) {
      scheduleAutoRemove(payload.transferId);
    }
  },

  removeTransfer: (transferId) =>
    set((s) => ({ transfers: s.transfers.filter((t) => t.transferId !== transferId) })),

  clearFinishedTransfers: () =>
    set((s) => ({ transfers: s.transfers.filter((t) => !t.done) })),

  reset: () => {
    clearAutoRemoveTimers();
    set((s) => ({ ...initialState, generation: (s.generation || 0) + 1 }));
  },
}));

// The passphrase gate unlocking the vault (for ANY reason — FTP's own Unlock
// button, the Vault panel, the Terminal picker) means the connection list can
// now be read: reload immediately so no surface needs a second Unlock click.
if (typeof window !== 'undefined') {
  window.addEventListener(VAULT_GATE_UNLOCKED_EVENT, () => {
    if (isTauri()) useFtpStore.getState().loadConnections();
  });
}

// Stable module-level setters/actions (callable without hooks).
export const setFtpActiveConnId = (v) => useFtpStore.getState().setActiveConnId(v);
export const loadFtpConnections = () => useFtpStore.getState().loadConnections();
export const upsertFtpTransfer = (payload) => useFtpStore.getState().upsertTransfer(payload);
export const resetFtpStore = () => useFtpStore.getState().reset();
